import { createAsyncThunk } from '@reduxjs/toolkit';
import { getToken, removeToken, removeRefreshToken, removeUserData, setToken, setRefreshToken } from '../../utils/HelperFunctions';
import api from '../../services/api';
import { NO_TOKEN_CODE, type LoginFailure, loginFailureFrom } from 'components/modules/auth/loginFailure';
import { LOGIN_TIMEOUT_MS } from 'components/modules/auth/signInRequest';

export const fetchUserData = createAsyncThunk('auth/fetchUserData', async (_, { rejectWithValue }) => {
  try {
    const accessToken = getToken();
    api.defaults.headers.Authorization = `Bearer ${accessToken}`;
    const response = await api.post('/auth/validatetoken');
    return { ...response.data, accessToken };
  } catch (e) {
    removeToken();
    removeRefreshToken();
    return rejectWithValue('');
  }
});

type LoginPayload = { username: string; password: string };

/**
 * What `POST /users/login` answers a correct password with.
 *
 * The backend's `generateAuthTokens` returns exactly these two
 * (`modules/authentication.js`). The id-token fields the SSO path carries are
 * not here and never were: they arrive through `loginSuccess`.
 */
export interface LoginResponse {
  token: string;
  refreshToken?: string;
}

export const login = createAsyncThunk<LoginResponse, LoginPayload, { rejectValue: LoginFailure }>('auth/login', async (payload, { rejectWithValue }) => {
  let response;
  try {
    response = await api.post('/users/login', payload, { timeout: LOGIN_TIMEOUT_MS });
  } catch (error) {
    // Reject with a sanitised descriptor rather than letting the error itself
    // become the rejected action: the caught axios error carries `config`, and
    // for this request that is the submitted credentials (issue #1181). The
    // status is data here, not a substring of an English message the caller
    // has to parse back out.
    return rejectWithValue(loginFailureFrom(error));
  }

  // A 200 carrying no token is a failed login that would otherwise succeed
  // silently: `setToken(undefined)` writes the STRING "undefined", which is
  // truthy, so the session looks valid and every later request 401s with no
  // explanation. Treated as a failure at the only point that can still tell.
  const token = response.data?.token;
  if (typeof token !== 'string' || !token) {
    return rejectWithValue({ status: response.status, code: NO_TOKEN_CODE });
  }

  setToken(token);
  if (response.data.refreshToken) {
    setRefreshToken(response.data.refreshToken);
  }
  localStorage.setItem('SSOLogin', 'false');
  api.defaults.headers.Authorization = `Bearer ${token}`;

  return response.data;
});

export const signOut = createAsyncThunk('auth/signOut', async () => {
  removeToken();
  removeRefreshToken();
  removeUserData();
  localStorage.setItem('SSOLogin', 'false');
});
